import { useEffect, useMemo, useState } from 'react';
import {
    Anchor,
    Button,
    Group,
    Modal,
    SimpleGrid,
    Stack,
    Tabs,
    Text,
} from '@mantine/core';
import { type DatesRangeValue } from '@mantine/dates';
import { FinanceDateRangeInput } from '@repo/date-range-picker';
import { Button as AppButton, Input, Pagination, QuietIconButton, Select as AppSelect, Textarea as AppTextarea } from '@shared/ui';
import '@mantine/dates/styles.css';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IoCashOutline, IoCheckmark, IoChevronDown, IoChevronForward, IoClose, IoCreateOutline, IoWalletOutline } from 'react-icons/io5';
import { Link, useLocation } from 'react-router-dom';
import { Breadcrumbs, OptionsTable } from '@components';
import type { OptionsTableColumn } from '@components';
import {
    sellerFinanceApi,
    type PlatformLedgerEntryRow,
    type SellerFinanceBatchDetail,
    type SellerFinanceBatchSummary,
    type SellerFinanceIntegrationKind,
    type SellerFinanceSettledSellerTotalsRow,
    type SellerManualAdjustmentRow,
} from '@modules/seller-finance/api/seller-finance.api';
import { sellersApi } from '@modules/sellers/api/sellers.api';
import { formatDate, formatPrice } from '@shared/utils';
import '@shared/styles/page.css';
import { PlatformAdvertisingPanel } from './PlatformAdvertisingPanel';
import './SellerFinancePage.css';

/** Календарная дата → `YYYY-MM-DD` для API (день по локальному календарю, как в Mantine DatePicker). */
function dateToQueryParam(d: Date | string): string {
    const dt = d instanceof Date ? d : new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function batchStatusLabel(status: string): string {
    switch (status) {
        case 'PENDING_EXECUTION':
            return 'Ожидает запуска';
        case 'EXECUTING':
            return 'В процессе';
        case 'COMPLETED':
            return 'Выполнена';
        case 'FAILED':
            return 'Ошибка';
        default:
            return status;
    }
}

function payoutStatusLabel(status: string): string {
    switch (status) {
        case 'PENDING':
            return 'Ожидает отправки';
        case 'PROCESSING':
            return 'Отправлено в банк';
        case 'COMPLETED':
            return 'Выплачено';
        case 'FAILED':
            return 'Ошибка';
        default:
            return status;
    }
}

function formatLedgerMoney(amount: number): string {
    return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

function platformKindLabel(kind: string, meta?: unknown): string {
    switch (kind) {
        case 'COMMISSION_INCOME': {
            const source =
                meta && typeof meta === 'object' ? (meta as { source?: unknown }).source : undefined;
            if (source === 'acquiring') return 'Эквайринг';
            if (source === 'refund_acquiring') return 'Эквайринг при возврате';
            return 'Комиссия маркетплейса';
        }
        case 'ADVERTISING_INCOME':
            return 'Реклама';
        case 'SELLER_PENALTY_INCOME':
            return 'Штраф';
        case 'MARKETPLACE_DELIVERY_INCOME':
            return 'Доставка от клиента';
        case 'MARKETPLACE_DELIVERY_COST':
            return 'Себестоимость доставки';
        case 'SELLER_DELIVERY_COMPENSATION_INCOME':
            return 'Компенсация доставки';
        case 'DOSTAVISTA_LATE_CANCEL_COST':
            return 'Штраф Достависты';
        case 'ORDER_ACCRUAL_STORNO': {
            const reversed =
                meta && typeof meta === 'object'
                    ? (meta as { reversedKinds?: unknown }).reversedKinds
                    : undefined;
            const kinds = Array.isArray(reversed)
                ? [...new Set(reversed.filter((k): k is string => typeof k === 'string'))]
                : [];
            if (kinds.length === 1 && kinds[0] === 'MARKETPLACE_DELIVERY_INCOME') {
                return 'Возврат клиенту за доставку';
            }
            if (kinds.length === 0) return 'Сторно';
            return `Сторно: ${kinds.map((k) => platformKindLabel(k).toLowerCase()).join(', ')}`;
        }
        case 'MANUAL_ADJUSTMENT':
            return 'Ручная корректировка';
        default:
            return kind;
    }
}

const PLATFORM_LEDGER_PAGE_SIZE = 25;

const FINANCE_TAB_STORAGE_KEY = 'seller-finance-admin-tab';
const FINANCE_TABS = ['schedule', 'platform', 'seller-totals', 'adjustments', 'batches'] as const;
type FinanceTab = (typeof FINANCE_TABS)[number];

function readStoredFinanceTab(): FinanceTab {
    try {
        const raw = localStorage.getItem(FINANCE_TAB_STORAGE_KEY);
        if (raw && (FINANCE_TABS as readonly string[]).includes(raw)) {
            return raw as FinanceTab;
        }
    } catch {
        // ignore
    }
    return 'schedule';
}

const WEEKDAYS = [
    { value: '1', label: 'Понедельник' },
    { value: '2', label: 'Вторник' },
    { value: '3', label: 'Среда' },
    { value: '4', label: 'Четверг' },
    { value: '5', label: 'Пятница' },
    { value: '6', label: 'Суббота' },
    { value: '7', label: 'Воскресенье' },
];

function getOtherDeductionValue(otherDeductions: unknown, key: string): number {
    if (!otherDeductions || typeof otherDeductions !== 'object') return 0;
    const raw = (otherDeductions as Record<string, unknown>)[key];
    const num =
        typeof raw === 'number'
            ? raw
            : typeof raw === 'string'
                ? Number(raw)
                : 0;
    return Number.isFinite(num) ? num : 0;
}

function settledCell(row: { id: number | string }, value: React.ReactNode) {
    return row.id === '__total-settled' ? (
        <span className="seller-finance-amount">{value}</span>
    ) : (
        value
    );
}

export default function SellerFinancePage() {
    const qc = useQueryClient();
    const { data: config, isLoading: configLoading } = useQuery({
        queryKey: ['seller-finance', 'config'],
        queryFn: () => sellerFinanceApi.getConfig(),
    });

    const {
        data: batches = [],
        isLoading: batchesLoading,
        isError: batchesIsError,
        refetch: refetchBatches,
    } = useQuery({
        queryKey: ['seller-finance', 'batches'],
        queryFn: () => sellerFinanceApi.listPayoutBatches(),
    });

    const [summaryPeriodRange, setSummaryPeriodRange] = useState<DatesRangeValue>([null, null]);
    const [appliedSummaryFrom, setAppliedSummaryFrom] = useState<string | undefined>();
    const [appliedSummaryTo, setAppliedSummaryTo] = useState<string | undefined>();

    const [platformView, setPlatformView] = useState<'operations' | 'advertising'>('operations');
    const [platformPeriodRange, setPlatformPeriodRange] = useState<DatesRangeValue>([null, null]);
    const [appliedPlatformFrom, setAppliedPlatformFrom] = useState<string | undefined>();
    const [appliedPlatformTo, setAppliedPlatformTo] = useState<string | undefined>();

    const onPeriodChange = (
        range: DatesRangeValue,
        setRange: (value: DatesRangeValue) => void,
        setFrom: (value: string | undefined) => void,
        setTo: (value: string | undefined) => void,
    ) => {
        setRange(range);
        const [fromDt, toDt] = range;
        const from = fromDt ? dateToQueryParam(fromDt) : undefined;
        const to = toDt ? dateToQueryParam(toDt) : undefined;
        if (from && to) {
            setFrom(from <= to ? from : to);
            setTo(from <= to ? to : from);
            return;
        }
        if (!from && !to) {
            setFrom(undefined);
            setTo(undefined);
        }
    };

    const {
        data: platformPnl,
        isLoading: platformPnlLoading,
        isError: platformPnlIsError,
        refetch: refetchPlatformPnl,
    } = useQuery({
        queryKey: ['seller-finance', 'platform-pnl', appliedPlatformFrom, appliedPlatformTo],
        queryFn: () =>
            sellerFinanceApi.getPlatformPnl({
                periodFrom: appliedPlatformFrom,
                periodTo: appliedPlatformTo,
            }),
    });

    const {
        data: platformLedger = [],
        isLoading: platformLedgerLoading,
        isError: platformLedgerIsError,
        refetch: refetchPlatformLedger,
    } = useQuery({
        queryKey: ['seller-finance', 'platform-ledger', appliedPlatformFrom, appliedPlatformTo],
        queryFn: () =>
            sellerFinanceApi.getPlatformLedger({
                periodFrom: appliedPlatformFrom,
                periodTo: appliedPlatformTo,
                limit: 500,
            }),
    });

    const [platformLedgerPage, setPlatformLedgerPage] = useState(1);

    useEffect(() => {
        setPlatformLedgerPage(1);
    }, [appliedPlatformFrom, appliedPlatformTo]);

    const platformLedgerTotalPages = useMemo(
        () => Math.max(1, Math.ceil(platformLedger.length / PLATFORM_LEDGER_PAGE_SIZE)),
        [platformLedger.length],
    );

    useEffect(() => {
        if (platformLedgerPage <= platformLedgerTotalPages) return;
        setPlatformLedgerPage(platformLedgerTotalPages);
    }, [platformLedgerPage, platformLedgerTotalPages]);

    const platformLedgerPageRows = useMemo(() => {
        const start = (platformLedgerPage - 1) * PLATFORM_LEDGER_PAGE_SIZE;
        return platformLedger.slice(start, start + PLATFORM_LEDGER_PAGE_SIZE);
    }, [platformLedger, platformLedgerPage]);

    const platformLedgerColumns = useMemo<OptionsTableColumn<PlatformLedgerEntryRow>[]>(
        () => [
            {
                key: 'occurredAt',
                label: 'Дата',
                render: (row) => new Date(row.occurredAt).toLocaleString('ru-RU'),
            },
            {
                key: 'kind',
                label: 'Операция',
                render: (row) => (
                    <span className="seller-finance-op-label">{platformKindLabel(row.kind, row.meta)}</span>
                ),
            },
            {
                key: 'shopName',
                label: 'Продавец',
                render: (row) =>
                    row.sellerId != null ? (
                        <Anchor
                            component={Link}
                            to={`/sellers/${row.sellerId}`}
                            state={{ from: 'seller-finance' }}
                            className="seller-finance-cell-link"
                        >
                            {row.shopName ?? `#${row.sellerId}`}
                        </Anchor>
                    ) : (
                        <span className="seller-finance-muted">—</span>
                    ),
            },
            {
                key: 'orderNumber',
                label: 'Заказ',
                render: (row) =>
                    row.orderId != null ? (
                        <Anchor
                            component={Link}
                            to={`/orders/${row.orderId}`}
                            state={{ from: 'seller-finance', tab: 'platform' }}
                            className="seller-finance-cell-link"
                        >
                            {row.orderNumber ?? row.orderId}
                        </Anchor>
                    ) : (
                        <span className="seller-finance-muted">—</span>
                    ),
            },
            {
                key: 'amountRub',
                label: 'Сумма',
                render: (row) => (
                    <span
                        className={
                            row.amountRub >= 0
                                ? 'seller-finance-amount seller-finance-amount--plus'
                                : 'seller-finance-amount seller-finance-amount--minus'
                        }
                    >
                        {row.amountRub >= 0 ? '+' : ''}
                        {formatLedgerMoney(row.amountRub)}
                    </span>
                ),
            },
        ],
        [],
    );

    const {
        data: settledTotals,
        isLoading: settledTotalsLoading,
        isError: settledTotalsIsError,
        refetch: refetchSettledTotals,
    } = useQuery({
        queryKey: [
            'seller-finance',
            'settled-payout-totals-by-seller',
            appliedSummaryFrom,
            appliedSummaryTo,
        ],
        queryFn: () =>
            sellerFinanceApi.getSettledPayoutTotalsBySeller(
                appliedSummaryFrom && appliedSummaryTo
                    ? { periodFrom: appliedSummaryFrom, periodTo: appliedSummaryTo }
                    : {},
            ),
    });

    const [expandedBatchId, setExpandedBatchId] = useState<number | null>(null);
    const { data: batchDetail, isPending: batchDetailPending } = useQuery({
        queryKey: ['seller-finance', 'batch', expandedBatchId],
        queryFn: () => sellerFinanceApi.getPayoutBatch(expandedBatchId!),
        enabled: expandedBatchId != null,
    });

    const toggleBatchRow = (batchId: number) => {
        setExpandedBatchId((prev) => (prev === batchId ? null : batchId));
    };

    const detailMatches =
        batchDetail != null &&
        expandedBatchId != null &&
        batchDetail.id === expandedBatchId;
    const batchDetailLoading =
        expandedBatchId != null && batchDetailPending && !detailMatches;

    type SettledRow = SellerFinanceSettledSellerTotalsRow & {
        id: number | string;
        rank?: number;
    };

    const settledTableData = useMemo((): SettledRow[] => {
        if (!settledTotals?.rows?.length) return [];
        const rows: SettledRow[] = settledTotals.rows.map((r, idx) => ({
            ...r,
            id: r.sellerId,
            rank: idx + 1,
        }));
        rows.push({
            id: '__total-settled',
            sellerId: 0,
            shopName: '',
            slug: '',
            payoutLinesCount: settledTotals.grandTotals.payoutLinesCount,
            ordersCount: settledTotals.grandTotals.ordersCount,
            itemsSold: settledTotals.grandTotals.itemsSold,
            grossRevenue: settledTotals.grandTotals.grossRevenue,
            commissionAmount: settledTotals.grandTotals.commissionAmount,
            promotionDeduction: settledTotals.grandTotals.promotionDeduction,
            advertisingDeduction: settledTotals.grandTotals.advertisingDeduction,
            sellerCancelPenalty: settledTotals.grandTotals.sellerCancelPenalty,
            deliveryDeduction: settledTotals.grandTotals.deliveryDeduction,
            netToSeller: settledTotals.grandTotals.netToSeller,
        });
        return rows;
    }, [settledTotals]);

    const settledColumns = useMemo<OptionsTableColumn<SettledRow>[]>(
        () => [
            {
                key: 'rank',
                label: '#',
                style: { width: 44 },
                render: (row) =>
                    row.id === '__total-settled' ? (
                        <span className="seller-finance-op-label">Итого</span>
                    ) : (
                        (row.rank ?? '')
                    ),
            },
            {
                key: 'shopName',
                label: 'Магазин',
                render: (row) =>
                    row.id === '__total-settled' ? null : (
                        <Anchor
                            component={Link}
                            to={`/sellers/${row.sellerId}`}
                            state={{ from: 'seller-finance' }}
                            className="seller-finance-cell-link"
                        >
                            {row.shopName}
                        </Anchor>
                    ),
            },
            {
                key: 'payoutLinesCount',
                label: 'Выплат (периодов)',
                render: (row) => settledCell(row, row.payoutLinesCount),
            },
            {
                key: 'ordersCount',
                label: 'Заказов Σ',
                render: (row) => settledCell(row, row.ordersCount),
            },
            {
                key: 'itemsSold',
                label: 'Продано шт.',
                render: (row) => settledCell(row, row.itemsSold),
            },
            {
                key: 'grossRevenue',
                label: 'Выручка',
                render: (row) => settledCell(row, formatPrice(row.grossRevenue)),
            },
            {
                key: 'commissionAmount',
                label: 'Комиссия',
                render: (row) => settledCell(row, formatPrice(row.commissionAmount)),
            },
            {
                key: 'promotionDeduction',
                label: 'Продв.',
                render: (row) => settledCell(row, formatPrice(row.promotionDeduction)),
            },
            {
                key: 'deliveryDeduction',
                label: 'Доставка уд.',
                render: (row) => settledCell(row, formatPrice(row.deliveryDeduction)),
            },
            {
                key: 'sellerCancelPenalty',
                label: 'Штрафы',
                render: (row) => settledCell(row, formatPrice(row.sellerCancelPenalty)),
            },
            {
                key: 'netToSeller',
                label: 'К выплате продавцу',
                render: (row) => settledCell(row, formatPrice(row.netToSeller)),
            },
        ],
        [],
    );

    const batchColumns = useMemo<OptionsTableColumn<SellerFinanceBatchSummary>[]>(
        () => [
            {
                key: '_expand',
                label: '',
                style: { width: 36 },
                render: (b) => (
                    <span aria-hidden style={{ display: 'flex' }}>
                        {expandedBatchId === b.id ? <IoChevronDown size={18} /> : <IoChevronForward size={18} />}
                    </span>
                ),
            },
            {
                key: 'id',
                label: 'ID пачки',
            },
            {
                key: 'period',
                label: 'Период начислений (UTC)',
                render: (b) => (
                    <>
                        {formatDate(b.accrualPeriodStart)} — {formatDate(b.accrualPeriodEndExclusive)}
                    </>
                ),
            },
            {
                key: 'status',
                label: 'Статус',
                render: (b) => batchStatusLabel(b.status),
            },
            {
                key: 'failureMessage',
                label: 'Ошибка',
                render: (b) => (
                    <Text size="xs" c={b.failureMessage ? 'red' : 'dimmed'}>
                        {b.failureMessage ?? '—'}
                    </Text>
                ),
            },
            { key: 'payoutsCount', label: 'Выплат продавцам' },
            {
                key: 'netTotal',
                label: 'Сумма к выплате',
                render: (b) => formatPrice(b.netTotal),
            },
            {
                key: 'executedAt',
                label: 'Выполнено',
                render: (b) => (b.executedAt ? formatDate(b.executedAt) : '—'),
            },
        ],
        [expandedBatchId],
    );

    const retryPayout = useMutation({
        mutationFn: sellerFinanceApi.retryPayout,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['seller-finance'] });
            notifications.show({
                title: 'Повтор',
                message: 'Выплата снова отправлена в Т-Банк',
                color: 'green',
            });
        },
        onError: (err: unknown) => {
            const msg =
                err && typeof err === 'object' && 'response' in err
                    ? (err as { response?: { data?: { message?: string } } }).response?.data
                        ?.message
                    : undefined;
            notifications.show({
                title: 'Не удалось повторить',
                message: msg ?? 'Ошибка запроса',
                color: 'red',
            });
        },
    });

    const payoutDetailColumns = useMemo<
        OptionsTableColumn<SellerFinanceBatchDetail['payouts'][number]>[]
    >(
        () => [
            {
                key: 'shopName',
                label: 'Продавец',
                render: (p) => (
                    <Anchor
                        component={Link}
                        to={`/sellers/${p.sellerId}`}
                        state={{ from: 'seller-finance' }}
                        size="sm"
                    >
                        {p.shopName}
                    </Anchor>
                ),
            },
            { key: 'ordersCount', label: 'Заказов' },
            { key: 'itemsSold', label: 'Продано шт.' },
            {
                key: 'grossRevenue',
                label: 'Выручка',
                render: (p) => formatPrice(p.grossRevenue),
            },
            {
                key: 'commissionAmount',
                label: 'Комиссия',
                render: (p) => formatPrice(p.commissionAmount),
            },
            {
                key: 'promotionDeduction',
                label: 'Продв.',
                render: (p) => {
                    const adv = getOtherDeductionValue(
                        p.otherDeductions,
                        'advertisingDeductionRub',
                    );
                    return formatPrice(p.promotionDeduction + adv);
                },
            },
            {
                key: 'sellerCancelPenalty',
                label: 'Штрафы',
                render: (p) =>
                    formatPrice(
                        getOtherDeductionValue(
                            p.otherDeductions,
                            'sellerCancelPenaltyRub',
                        ),
                    ),
            },
            {
                key: 'deliveryDeduction',
                label: 'Доставка',
                render: (p) =>
                    formatPrice(
                        p.deliveryDeduction ||
                        getOtherDeductionValue(p.otherDeductions, 'deliveryDeductionRub'),
                    ),
            },
            {
                key: 'priorBalance',
                label: 'Перенос',
                render: (p) => {
                    const v = getOtherDeductionValue(p.otherDeductions, 'priorBalanceRub');
                    if (!v) return '—';
                    return formatPrice(v);
                },
            },
            {
                key: 'netAmount',
                label: 'К выплате',
                render: (p) => formatPrice(p.netAmount),
            },
            {
                key: 'status',
                label: 'Статус',
                render: (p) => payoutStatusLabel(p.status),
            },
            {
                key: 'paid',
                label: 'Выплачено',
                render: (p) => (p.paidAt && p.status === 'COMPLETED' ? 'Да' : 'Нет'),
            },
            {
                key: 'externalPaymentId',
                label: 'T-Bank id',
                render: (p) => (
                    <Text size="xs">{p.externalPaymentId ?? '—'}</Text>
                ),
            },
            {
                key: 'comment',
                label: 'Комментарий',
                render: (p) => (
                    <Text size="xs" c={p.status === 'FAILED' ? 'red' : 'dimmed'} lineClamp={2}>
                        {p.comment ?? '—'}
                    </Text>
                ),
            },
            {
                key: 'tbankReceiptUrl',
                label: 'Чек НПД',
                render: (p) =>
                    p.tbankReceiptUrl ? (
                        <Anchor href={p.tbankReceiptUrl} target="_blank" rel="noreferrer" size="xs">
                            Мой налог
                        </Anchor>
                    ) : (
                        <Text size="xs" c="dimmed">
                            —
                        </Text>
                    ),
            },
            {
                key: 'retry',
                label: '',
                render: (p) =>
                    p.status === 'FAILED' ? (
                        <Button
                            size="compact-xs"
                            variant="light"
                            onClick={(e) => {
                                e.stopPropagation();
                                retryPayout.mutate(p.id);
                            }}
                            loading={retryPayout.isPending}
                        >
                            Повторить
                        </Button>
                    ) : null,
            },
        ],
        [retryPayout],
    );

    const [weekday, setWeekday] = useState('5');
    const [hour, setHour] = useState<number | string>(10);
    const [minute, setMinute] = useState<number | string>(0);
    const [integrationKind, setIntegrationKind] =
        useState<SellerFinanceIntegrationKind>('MOCK');
    const [scheduleEditing, setScheduleEditing] = useState(false);
    const scheduleLocked = configLoading || !scheduleEditing;

    useEffect(() => {
        if (!config || scheduleEditing) return;
        setWeekday(String(config.payoutWeekdayUtc));
        setHour(config.payoutHourUtc);
        setMinute(config.payoutMinuteUtc);
        setIntegrationKind(config.integrationKind);
    }, [config, scheduleEditing]);

    const saveConfig = useMutation({
        mutationFn: sellerFinanceApi.patchConfig,
        onSuccess: () => {
            setScheduleEditing(false);
            qc.invalidateQueries({ queryKey: ['seller-finance', 'config'] });
            notifications.show({ title: 'Сохранено', message: 'Расписание обновлено', color: 'green' });
        },
        onError: () =>
            notifications.show({ title: 'Ошибка', message: 'Не удалось сохранить', color: 'red' }),
    });

    const mockWeekError = (err: unknown) => {
        const msg =
            err && typeof err === 'object' && 'response' in err
                ? (err as { response?: { data?: { message?: string } } }).response?.data
                    ?.message
                : undefined;
        notifications.show({
            title: 'Не выполнено',
            message: msg ?? 'Возможно, за этот период выплата уже есть',
            color: 'red',
        });
    };

    const mockWeek = useMutation({
        mutationFn: sellerFinanceApi.mockPreviousWeek,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['seller-finance'] });
            notifications.show({
                title: config?.integrationKind === 'TBANK_BULK_OUTBOX' ? 'Выплата в Т-Банк' : 'Мок выплаты',
                message:
                    config?.integrationKind === 'TBANK_BULK_OUTBOX'
                        ? 'Пачка создана, платежи поставлены в очередь T-API'
                        : 'Пачка за прошлую неделю (UTC) создана и помечена как проведённая',
                color: 'green',
            });
        },
        onError: mockWeekError,
    });

    const mockCurrentWeek = useMutation({
        mutationFn: sellerFinanceApi.mockCurrentWeek,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['seller-finance'] });
            notifications.show({
                title: config?.integrationKind === 'TBANK_BULK_OUTBOX' ? 'Выплата в Т-Банк' : 'Мок выплаты',
                message:
                    config?.integrationKind === 'TBANK_BULK_OUTBOX'
                        ? 'Пачка за текущую неделю создана, платежи в очереди T-API'
                        : 'Пачка за текущую неделю (UTC) создана и помечена как проведённая',
                color: 'green',
            });
        },
        onError: mockWeekError,
    });

    const syncTbank = useMutation({
        mutationFn: sellerFinanceApi.syncTbankPayouts,
        onSuccess: (data) => {
            qc.invalidateQueries({ queryKey: ['seller-finance'] });
            notifications.show({
                title: 'Синхронизация T-API',
                message: `Отправлено ${data.submitted}, исполнено ${data.completed}, ошибок ${data.failed}`,
                color: 'green',
            });
        },
        onError: (err: unknown) => {
            const msg =
                err && typeof err === 'object' && 'response' in err
                    ? (err as { response?: { data?: { message?: string } } }).response?.data
                        ?.message
                    : undefined;
            notifications.show({
                title: 'Синхронизация не удалась',
                message: msg ?? 'Ошибка запроса',
                color: 'red',
            });
        },
    });

    const location = useLocation();
    const navTab = (location.state as { tab?: string } | null)?.tab;
    const [financeTab, setFinanceTab] = useState<string>(() => {
        if (navTab && (FINANCE_TABS as readonly string[]).includes(navTab)) {
            try {
                localStorage.setItem(FINANCE_TAB_STORAGE_KEY, navTab);
            } catch {
                // ignore
            }
            return navTab;
        }
        return readStoredFinanceTab();
    });

    const handleFinanceTabChange = (v: string | null) => {
        const next = v && (FINANCE_TABS as readonly string[]).includes(v) ? v : 'schedule';
        setFinanceTab(next);
        try {
            localStorage.setItem(FINANCE_TAB_STORAGE_KEY, next);
        } catch {
            // ignore
        }
    };

    const [adjustSellerId, setAdjustSellerId] = useState<string | null>(null);
    const [adjustType, setAdjustType] = useState<'debit' | 'credit'>('debit');
    const [adjustAmount, setAdjustAmount] = useState<number | string>('');
    const [adjustComment, setAdjustComment] = useState('');

    const { data: sellers = [], isLoading: sellersLoading } = useQuery({
        queryKey: ['sellers', 'list-for-finance-adjustments'],
        queryFn: () => sellersApi.getAll(),
        enabled: financeTab === 'adjustments',
    });

    const {
        data: manualAdjustments = [],
        isLoading: manualAdjustmentsLoading,
        isError: manualAdjustmentsIsError,
        refetch: refetchManualAdjustments,
    } = useQuery({
        queryKey: ['seller-finance', 'manual-adjustments'],
        queryFn: () => sellerFinanceApi.listManualAdjustments(100),
        enabled: financeTab === 'adjustments',
    });

    const sellerSelectOptions = useMemo(
        () =>
            sellers.map((s) => ({
                value: String(s.id),
                label: s.shopName?.trim() ? `${s.shopName} (#${s.id})` : `Магазин #${s.id}`,
            })),
        [sellers],
    );

    const createManualAdjustment = useMutation({
        mutationFn: sellerFinanceApi.createManualAdjustment,
        onSuccess: (data) => {
            qc.invalidateQueries({ queryKey: ['seller-finance'] });
            setAdjustAmount('');
            setAdjustComment('');
            notifications.show({
                title: data.type === 'debit' ? 'Списание выполнено' : 'Начисление выполнено',
                message: `Баланс продавца сейчас: ${formatPrice(data.currentBalanceRub)}`,
                color: 'green',
            });
        },
        onError: (err: unknown) => {
            const msg =
                err && typeof err === 'object' && 'response' in err
                    ? (err as { response?: { data?: { message?: string | string[] } } }).response
                          ?.data?.message
                    : undefined;
            const text = Array.isArray(msg) ? msg.join(', ') : msg;
            notifications.show({
                title: 'Не удалось провести корректировку',
                message: text ?? 'Ошибка запроса',
                color: 'red',
            });
        },
    });

    const onSubmitManualAdjustment = () => {
        const sellerId = adjustSellerId ? Number(adjustSellerId) : NaN;
        const amount =
            typeof adjustAmount === 'string' ? parseFloat(adjustAmount) : Number(adjustAmount);
        const comment = adjustComment.trim();
        if (!Number.isFinite(sellerId) || sellerId < 1) {
            notifications.show({
                title: 'Продавец',
                message: 'Выберите магазин',
                color: 'orange',
            });
            return;
        }
        if (!Number.isFinite(amount) || amount <= 0) {
            notifications.show({
                title: 'Сумма',
                message: 'Укажите сумму больше 0',
                color: 'orange',
            });
            return;
        }
        if (comment.length < 3) {
            notifications.show({
                title: 'Комментарий',
                message: 'Укажите причину (минимум 3 символа)',
                color: 'orange',
            });
            return;
        }
        createManualAdjustment.mutate({
            sellerId,
            type: adjustType,
            amountRub: Math.round(amount * 100) / 100,
            comment,
        });
    };

    const manualAdjustmentColumns = useMemo<OptionsTableColumn<SellerManualAdjustmentRow>[]>(
        () => [
            {
                key: 'occurredAt',
                label: 'Дата',
                render: (row) => new Date(row.occurredAt).toLocaleString('ru-RU'),
            },
            {
                key: 'shopName',
                label: 'Продавец',
                render: (row) => (
                    <Anchor
                        component={Link}
                        to={`/sellers/${row.sellerId}`}
                        state={{ from: 'seller-finance' }}
                        className="seller-finance-cell-link"
                    >
                        {row.shopName || `#${row.sellerId}`}
                    </Anchor>
                ),
            },
            {
                key: 'type',
                label: 'Операция',
                render: (row) => (
                    <span className="seller-finance-op-label">
                        {row.type === 'debit' ? 'Списание / штраф' : 'Начисление / возврат'}
                    </span>
                ),
            },
            {
                key: 'signedAmountRub',
                label: 'Сумма',
                render: (row) => (
                    <span
                        className={
                            row.signedAmountRub >= 0
                                ? 'seller-finance-amount seller-finance-amount--plus'
                                : 'seller-finance-amount seller-finance-amount--minus'
                        }
                    >
                        {row.signedAmountRub >= 0 ? '+' : ''}
                        {formatLedgerMoney(row.signedAmountRub)}
                    </span>
                ),
            },
            {
                key: 'comment',
                label: 'Комментарий',
                render: (row) =>
                    row.comment || row.title ? (
                        row.comment || row.title
                    ) : (
                        <span className="seller-finance-muted">—</span>
                    ),
            },
        ],
        [],
    );

    const [clearModalOpen, setClearModalOpen] = useState(false);
    const clearAllPayouts = useMutation({
        mutationFn: sellerFinanceApi.clearAllPayoutsForTest,
        onSuccess: (data) => {
            setClearModalOpen(false);
            qc.invalidateQueries({ queryKey: ['seller-finance'] });
            notifications.show({
                title: 'Очищено',
                message: `Удалено выплат: ${data.deletedPayouts}, пачек: ${data.deletedBatches}`,
                color: 'green',
            });
        },
        onError: (err: unknown) => {
            const msg =
                err && typeof err === 'object' && 'response' in err
                    ? (err as { response?: { data?: { message?: string } } }).response?.data
                        ?.message
                    : undefined;
            notifications.show({
                title: 'Не удалось очистить',
                message: msg ?? 'Ошибка запроса',
                color: 'red',
            });
        },
    });

    const onSaveSchedule = () => {
        const h = typeof hour === 'string' ? parseInt(hour, 10) : hour;
        const m = typeof minute === 'string' ? parseInt(minute, 10) : minute;
        const wd = parseInt(weekday, 10);
        if ([wd, h, m].some((x) => Number.isNaN(x))) return;
        saveConfig.mutate({
            payoutWeekdayUtc: wd,
            payoutHourUtc: Math.min(23, Math.max(0, h)),
            payoutMinuteUtc: Math.min(59, Math.max(0, m)),
            integrationKind,
        });
    };

    return (
        <div className="page-container seller-finance-page">
            <Breadcrumbs
                items={[
                    { label: 'Главная', path: '/' },
                    { label: 'Финансы' },
                ]}
            />
            <div className="seller-finance-head">
                <h1 className="seller-finance-title">Финансы</h1>
                <p className="seller-finance-subtitle">
                    Расписание выплат, выручка платформы и балансы продавцов
                </p>
            </div>

            <div className="seller-finance-page__scroll">
                <div className="content-wrapper seller-finance-page__content-wrapper">
                    <div className="seller-finance-page__content">
                        <Tabs
                            value={financeTab}
                            onChange={handleFinanceTabChange}
                            className="seller-finance-page__tabs seller-finance-tabs"
                        >
                            <Tabs.List className="seller-finance-page__tabs-list seller-finance-tabs-list">
                                <Tabs.Tab value="schedule" className="seller-finance-tabs-tab">
                                    Расписание и выплаты
                                </Tabs.Tab>
                                <Tabs.Tab value="platform" className="seller-finance-tabs-tab">
                                    Платформа
                                </Tabs.Tab>
                                <Tabs.Tab value="seller-totals" className="seller-finance-tabs-tab">
                                    Сводка по продавцам
                                </Tabs.Tab>
                                <Tabs.Tab value="adjustments" className="seller-finance-tabs-tab">
                                    Корректировки
                                </Tabs.Tab>
                                <Tabs.Tab value="batches" className="seller-finance-tabs-tab">
                                    Пачки выплат
                                </Tabs.Tab>
                            </Tabs.List>

                            <Tabs.Panel value="schedule" className="seller-finance-page__tabs-panel seller-finance-tabs-panel">
                                <section className="seller-dashboard-card seller-finance-card">
                                    <div className="seller-dashboard-card__head">
                                        <h2 className="seller-dashboard-card__title">Расписание выплат (UTC)</h2>
                                        <div className="seller-finance-schedule-actions">
                                            {!scheduleEditing ? (
                                                <QuietIconButton
                                                    icon={<IoCreateOutline size={18} />}
                                                    onClick={() => setScheduleEditing(true)}
                                                    disabled={configLoading}
                                                >
                                                    Редактировать
                                                </QuietIconButton>
                                            ) : (
                                                <>
                                                    <QuietIconButton
                                                        icon={<IoCheckmark size={18} />}
                                                        tone="accent"
                                                        onClick={onSaveSchedule}
                                                        loading={saveConfig.isPending}
                                                        aria-label="Сохранить"
                                                    />
                                                    <QuietIconButton
                                                        icon={<IoClose size={18} />}
                                                        onClick={() => setScheduleEditing(false)}
                                                        disabled={saveConfig.isPending}
                                                        aria-label="Отменить"
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <p className="seller-finance-note">
                                        Целевое время еженедельного прогона. При интеграции Т-Банк cron сам
                                        запускает выплату за прошлую неделю после этого времени (UTC).
                                        ООО и ИП — рублёвый перевод на р/с. Самозанятые — реестр T-API
                                        с проверкой НПД и чеком в «Мой налог».
                                    </p>
                                        <div className="seller-finance-schedule-form">
                                            <div className="seller-finance-schedule-form__weekday">
                                                <AppSelect
                                                    label="День недели"
                                                    data={WEEKDAYS}
                                                    value={weekday}
                                                    onChange={(value) => setWeekday(value == null ? weekday : String(value))}
                                                    disabled={scheduleLocked}
                                                    allowDeselect={false}
                                                />
                                            </div>
                                            <div className="seller-finance-schedule-form__time">
                                                <Input
                                                    label="Час"
                                                    type="number"
                                                    min="0"
                                                    max="23"
                                                    step="1"
                                                    value={hour === '' ? '' : String(hour)}
                                                    onChange={(e) => setHour(e.target.value)}
                                                    disabled={scheduleLocked}
                                                />
                                            </div>
                                            <div className="seller-finance-schedule-form__time">
                                                <Input
                                                    label="Минута"
                                                    type="number"
                                                    min="0"
                                                    max="59"
                                                    step="1"
                                                    value={minute === '' ? '' : String(minute)}
                                                    onChange={(e) => setMinute(e.target.value)}
                                                    disabled={scheduleLocked}
                                                />
                                            </div>
                                            <div className="seller-finance-schedule-form__integration">
                                                <AppSelect
                                                    label="Интеграция"
                                                    data={[
                                                        { value: 'MOCK', label: 'Мок (без банка)' },
                                                        {
                                                            value: 'TBANK_BULK_OUTBOX',
                                                            label: 'Т‑Банк (рублёвые выплаты T-API)',
                                                        },
                                                    ]}
                                                    value={integrationKind}
                                                    onChange={(value) => {
                                                        if (value === 'MOCK' || value === 'TBANK_BULK_OUTBOX') {
                                                            setIntegrationKind(value);
                                                        }
                                                    }}
                                                    disabled={scheduleLocked}
                                                    allowDeselect={false}
                                                />
                                            </div>
                                        </div>
                                        {config?.nextScheduledPayoutAt && (
                                            <p className="seller-finance-note seller-finance-note--flush">
                                                Ближайшее запланированное время (оценка):{' '}
                                                {formatDate(config.nextScheduledPayoutAt)}
                                            </p>
                                        )}
                                        {integrationKind === 'TBANK_BULK_OUTBOX' && (
                                            <p
                                                className={
                                                    config?.tbankOpenApiConfigured
                                                        ? 'seller-finance-note seller-finance-note--flush'
                                                        : 'seller-finance-note seller-finance-note--flush seller-finance-note--danger'
                                                }
                                            >
                                                {config?.tbankOpenApiConfigured
                                                    ? 'T-API выплат настроен (счёт плательщика и доступ к API).'
                                                    : 'T-API не настроен: задайте TBANK_PAYER_ACCOUNT_NUMBER, токен и mTLS (или TBANK_OPENAPI_SANDBOX=1).'}
                                            </p>
                                        )}
                                </section>

                                <section className="seller-dashboard-card seller-finance-card">
                                    <div className="seller-dashboard-card__head">
                                        <h2 className="seller-dashboard-card__title">Недельные выплаты (тест)</h2>
                                    </div>
                                    <p className="seller-finance-note">
                                        Пачки по ledger (пн–вс UTC). Мок сразу помечает строки проведёнными.
                                        Т-Банк ставит платежи в T-API и ждёт статус EXECUTED.
                                    </p>
                                        <Group gap="sm" align="center">
                                            <Button
                                                variant="filled"
                                                onClick={() => mockWeek.mutate()}
                                                loading={mockWeek.isPending}
                                            >
                                                За прошлую неделю (UTC)
                                            </Button>
                                            <Button
                                                variant="filled"
                                                color="teal"
                                                onClick={() => mockCurrentWeek.mutate()}
                                                loading={mockCurrentWeek.isPending}
                                            >
                                                За текущую неделю (UTC)
                                            </Button>
                                            {config?.integrationKind === 'TBANK_BULK_OUTBOX' && (
                                                <Button
                                                    variant="light"
                                                    onClick={() => syncTbank.mutate()}
                                                    loading={syncTbank.isPending}
                                                >
                                                    Синхронизировать статусы T-API
                                                </Button>
                                            )}
                                            <Button
                                                variant="light"
                                                color="red"
                                                onClick={() => setClearModalOpen(true)}
                                            >
                                                Очистить все выплаты (тест)
                                            </Button>
                                        </Group>
                                        <p className="seller-finance-note seller-finance-note--flush">
                                            «Прошлая» = 10–16 при сегодня 19.08; «текущая» = 17–23.
                                            Очистка удаляет все пачки — после этого можно снова нажать кнопки.
                                        </p>
                                </section>
                            </Tabs.Panel>

                            <Tabs.Panel value="platform" className="seller-finance-page__tabs-panel seller-finance-tabs-panel">
                                <p className="seller-finance-lead">
                                    Сверху — сколько денег на стороне платформы и сколько из них ещё должны
                                    продавцам. Ниже — из чего сложился заработок за период и журнал проводок;
                                    рекламные списания вынесены в отдельную вкладку по продавцам.
                                </p>

                                    {platformPnlLoading && !platformPnl ? (
                                        <Text size="sm" c="dimmed">
                                            Загрузка…
                                        </Text>
                                    ) : platformPnlIsError && !platformPnl ? (
                                        <Text size="sm" c="red">
                                            Не удалось загрузить.{' '}
                                            <Anchor component="button" type="button" onClick={() => refetchPlatformPnl()}>
                                                Повторить
                                            </Anchor>
                                        </Text>
                                    ) : platformPnl?.balances ? (
                                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                                            <div className="seller-dashboard-metric seller-dashboard-metric--accent">
                                                <div className="seller-dashboard-metric__top">
                                                    <Text className="seller-dashboard-metric__title">Всего на счету</Text>
                                                    <span className="seller-dashboard-metric__dot" aria-hidden>
                                                        <IoCashOutline />
                                                    </span>
                                                </div>
                                                <Text className="seller-dashboard-metric__value">
                                                    {formatPrice(platformPnl.balances.totalOnAccountRub)}
                                                </Text>
                                                <Text className="seller-dashboard-metric__hint">
                                                    Заработок платформы (
                                                    {formatPrice(platformPnl.balances.platformBalanceRub)}) + к выплате
                                                    продавцам ({formatPrice(platformPnl.balances.sellersPayableRub)})
                                                </Text>
                                            </div>
                                            <div className="seller-dashboard-metric seller-dashboard-metric--green">
                                                <div className="seller-dashboard-metric__top">
                                                    <Text className="seller-dashboard-metric__title">
                                                        К выплате продавцам
                                                    </Text>
                                                    <span className="seller-dashboard-metric__dot" aria-hidden>
                                                        <IoWalletOutline />
                                                    </span>
                                                </div>
                                                <Text className="seller-dashboard-metric__value">
                                                    {formatPrice(platformPnl.balances.sellersPayableRub)}
                                                </Text>
                                                <Text className="seller-dashboard-metric__hint">
                                                    Плюсовые балансы магазинов. Самовывоз при получении не входит.
                                                </Text>
                                            </div>
                                        </SimpleGrid>
                                    ) : null}

                                    <div className="seller-dashboard__period seller-finance-period">
                                        <Text className="seller-dashboard__period-label">Период</Text>
                                        <div className="seller-dashboard__period-dates">
                                            <FinanceDateRangeInput
                                                value={platformPeriodRange}
                                                onChange={(range) =>
                                                    onPeriodChange(
                                                        range,
                                                        setPlatformPeriodRange,
                                                        setAppliedPlatformFrom,
                                                        setAppliedPlatformTo,
                                                    )
                                                }
                                                valueFormat="DD.MM.YYYY"
                                                placeholder="Выберите даты"
                                                size="sm"
                                                radius="md"
                                            />
                                        </div>
                                    </div>
                                    {platformPnlLoading ? (
                                        <Text size="sm" c="dimmed">
                                            Загрузка итогов…
                                        </Text>
                                    ) : platformPnlIsError ? (
                                        <Text size="sm" c="red">
                                            Не удалось загрузить P&amp;L.{' '}
                                            <Anchor component="button" type="button" onClick={() => refetchPlatformPnl()}>
                                                Повторить
                                            </Anchor>
                                        </Text>
                                    ) : platformPnl ? (
                                        <section className="seller-dashboard-card seller-finance-card">
                                            <div className="seller-dashboard-card__head">
                                                <h2 className="seller-dashboard-card__title">
                                                    За период
                                                    {appliedPlatformFrom && appliedPlatformTo
                                                        ? ` ${appliedPlatformFrom} — ${appliedPlatformTo}`
                                                        : ' (всё время)'}
                                                </h2>
                                            </div>
                                            <div className="seller-finance-pnl">
                                                {platformPnl.lines.map((line) => (
                                                    <div key={line.kind} className="seller-finance-pnl__row">
                                                        <span>
                                                            {line.label}
                                                            {line.count > 0 ? (
                                                                <span className="seller-finance-muted"> ({line.count})</span>
                                                            ) : null}
                                                        </span>
                                                        <span
                                                            className={
                                                                line.amountRub < 0
                                                                    ? 'seller-finance-amount seller-finance-amount--minus'
                                                                    : 'seller-finance-amount'
                                                            }
                                                        >
                                                            {formatPrice(line.amountRub)}
                                                        </span>
                                                    </div>
                                                ))}
                                                <div className="seller-finance-pnl__row seller-finance-pnl__row--total">
                                                    <span>Итого за период</span>
                                                    <span
                                                        className={
                                                            platformPnl.net < 0
                                                                ? 'seller-finance-amount seller-finance-amount--minus'
                                                                : 'seller-finance-amount'
                                                        }
                                                    >
                                                        {formatPrice(platformPnl.net)}
                                                    </span>
                                                </div>
                                            </div>
                                        </section>
                                    ) : null}

                                    <Tabs
                                        value={platformView}
                                        onChange={(v) => setPlatformView(v === 'advertising' ? 'advertising' : 'operations')}
                                    >
                                        <Tabs.List className="seller-finance-tabs-list">
                                            <Tabs.Tab value="operations" className="seller-finance-tabs-tab">
                                                Операции
                                            </Tabs.Tab>
                                            <Tabs.Tab value="advertising" className="seller-finance-tabs-tab">
                                                Реклама
                                            </Tabs.Tab>
                                        </Tabs.List>
                                    </Tabs>

                                    {platformView === 'advertising' ? (
                                        <PlatformAdvertisingPanel
                                            periodFrom={appliedPlatformFrom}
                                            periodTo={appliedPlatformTo}
                                        />
                                    ) : (
                                    <section className="seller-dashboard-card seller-finance-card">
                                        <div className="seller-dashboard-card__head">
                                            <h2 className="seller-dashboard-card__title">Журнал проводок</h2>
                                        </div>
                                        <div className="seller-finance-table-wrap">
                                            <OptionsTable<PlatformLedgerEntryRow>
                                                columns={platformLedgerColumns}
                                                data={platformLedgerPageRows}
                                                loading={platformLedgerLoading}
                                                error={
                                                    platformLedgerIsError
                                                        ? new Error('Не удалось загрузить журнал')
                                                        : undefined
                                                }
                                                refetch={refetchPlatformLedger}
                                                emptyMessage="Пока нет проводок платформы — после бэкапа или новых заказов они появятся здесь."
                                            />
                                        </div>
                                        <Pagination
                                            page={platformLedgerPage}
                                            onChange={setPlatformLedgerPage}
                                            totalItems={platformLedger.length}
                                            pageSize={PLATFORM_LEDGER_PAGE_SIZE}
                                        />
                                    </section>
                                    )}
                            </Tabs.Panel>

                            <Tabs.Panel value="seller-totals" className="seller-finance-page__tabs-panel seller-finance-tabs-panel">
                                <div className="seller-dashboard__period seller-finance-period">
                                    <Text className="seller-dashboard__period-label">Период</Text>
                                    <div className="seller-dashboard__period-dates">
                                        <FinanceDateRangeInput
                                            value={summaryPeriodRange}
                                            onChange={(range) =>
                                                onPeriodChange(
                                                    range,
                                                    setSummaryPeriodRange,
                                                    setAppliedSummaryFrom,
                                                    setAppliedSummaryTo,
                                                )
                                            }
                                            valueFormat="DD.MM.YYYY"
                                            placeholder="Выберите даты"
                                            size="sm"
                                            radius="md"
                                        />
                                    </div>
                                </div>
                                <section className="seller-dashboard-card seller-finance-card">
                                    <div className="seller-dashboard-card__head">
                                        <h2 className="seller-dashboard-card__title">Сводка по продавцам</h2>
                                    </div>
                                    <p className="seller-finance-note">
                                        {appliedSummaryFrom != null && appliedSummaryTo != null
                                            ? `Фильтр по пачкам (пересечение периода начислений): ${appliedSummaryFrom} — ${appliedSummaryTo}`
                                            : 'Без фильтра по датам — все проведённые выплаты.'}
                                    </p>
                                    <div className="seller-finance-table-wrap">
                                        <OptionsTable<SettledRow>
                                            columns={settledColumns}
                                            data={settledTableData}
                                            loading={settledTotalsLoading}
                                            error={
                                                settledTotalsIsError
                                                    ? new Error('Не удалось загрузить данные')
                                                    : undefined
                                            }
                                            refetch={refetchSettledTotals}
                                            emptyMessage="Нет данных по завершённым выплатам — после проведённых пачек здесь появится сводка."
                                        />
                                    </div>
                                </section>
                            </Tabs.Panel>

                            <Tabs.Panel value="adjustments" className="seller-finance-page__tabs-panel seller-finance-tabs-panel">
                                <section className="seller-dashboard-card seller-finance-card">
                                    <div className="seller-dashboard-card__head">
                                        <h2 className="seller-dashboard-card__title">Корректировка баланса продавца</h2>
                                    </div>
                                    <p className="seller-finance-note">
                                        Ручные операции по балансу к выплате: штрафы, которые нельзя начислить
                                        автоматически, или возврат средств при ошибочном списании. Операция сразу
                                        меняет баланс продавца и отражается в P&L платформы.
                                    </p>
                                        <div className="seller-finance-adjust-form">
                                            <div className="seller-finance-adjust-form__row">
                                                <div className="seller-finance-adjust-form__seller">
                                                    <AppSelect
                                                        label="Продавец"
                                                        placeholder="Найдите магазин"
                                                        data={sellerSelectOptions}
                                                        value={adjustSellerId}
                                                        onChange={(value) =>
                                                            setAdjustSellerId(value == null ? null : String(value))
                                                        }
                                                        searchable
                                                        clearable
                                                        disabled={sellersLoading}
                                                        nothingFoundMessage="Продавцы не найдены"
                                                    />
                                                </div>
                                                <div className="seller-finance-adjust-form__type">
                                                    <AppSelect
                                                        label="Операция"
                                                        data={[
                                                            { value: 'debit', label: 'Списание / штраф' },
                                                            { value: 'credit', label: 'Начисление / возврат' },
                                                        ]}
                                                        value={adjustType}
                                                        onChange={(value) =>
                                                            setAdjustType(value === 'credit' ? 'credit' : 'debit')
                                                        }
                                                        allowDeselect={false}
                                                    />
                                                </div>
                                                <div className="seller-finance-adjust-form__amount">
                                                    <Input
                                                        label="Сумма, ₽"
                                                        type="number"
                                                        min="0.01"
                                                        step="0.01"
                                                        value={adjustAmount === '' ? '' : String(adjustAmount)}
                                                        onChange={(e) => setAdjustAmount(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <AppTextarea
                                                className="seller-finance-adjust-form__comment"
                                                label="Комментарий / причина"
                                                placeholder="Например: штраф за нарушение правил витрины"
                                                value={adjustComment}
                                                onChange={(e) => setAdjustComment(e.currentTarget.value)}
                                                minRows={2}
                                                autosize
                                            />
                                            <AppButton
                                                type="button"
                                                variant="primary"
                                                onClick={onSubmitManualAdjustment}
                                                loading={createManualAdjustment.isPending}
                                            >
                                                {adjustType === 'debit'
                                                    ? 'Списать с баланса'
                                                    : 'Начислить на баланс'}
                                            </AppButton>
                                        </div>
                                </section>

                                <section className="seller-dashboard-card seller-finance-card">
                                    <div className="seller-dashboard-card__head">
                                        <h2 className="seller-dashboard-card__title">История корректировок</h2>
                                    </div>
                                    <div className="seller-finance-table-wrap">
                                        <OptionsTable<SellerManualAdjustmentRow>
                                            columns={manualAdjustmentColumns}
                                            data={manualAdjustments}
                                            loading={manualAdjustmentsLoading}
                                            error={
                                                manualAdjustmentsIsError
                                                    ? new Error('Не удалось загрузить историю')
                                                    : undefined
                                            }
                                            refetch={refetchManualAdjustments}
                                            emptyMessage="Пока нет ручных корректировок"
                                        />
                                    </div>
                                </section>
                            </Tabs.Panel>

                            <Tabs.Panel value="batches" className="seller-finance-page__tabs-panel seller-finance-tabs-panel">
                                <section className="seller-dashboard-card seller-finance-card">
                                    <div className="seller-dashboard-card__head">
                                        <h2 className="seller-dashboard-card__title">Пачки выплат</h2>
                                    </div>
                                    <p className="seller-finance-note">
                                        Одна строка — недельная пачка. Нажмите строку, чтобы раскрыть под ней выплаты
                                        по продавцам; повторный клик сворачивает блок.
                                    </p>
                                    <div className="seller-finance-table-wrap">
                                    <OptionsTable<SellerFinanceBatchSummary>
                                        columns={batchColumns}
                                        data={batches}
                                        loading={batchesLoading}
                                        error={
                                            batchesIsError ? new Error('Не удалось загрузить данные') : undefined
                                        }
                                        refetch={refetchBatches}
                                        emptyMessage="Пока нет пачек"
                                        onRowClick={(b) => toggleBatchRow(b.id)}
                                        isRowExpanded={(b) => expandedBatchId === b.id}
                                        renderExpandedRow={() => (
                                            <div className="seller-finance-page__batch-detail-panel">
                                                {batchDetailLoading ? (
                                                    <Text size="sm" c="dimmed">
                                                        Загрузка…
                                                    </Text>
                                                ) : detailMatches && batchDetail ? (
                                                    <Stack gap="sm">
                                                        {batchDetail.memo && (
                                                            <Text size="xs" c="dimmed">
                                                                {batchDetail.memo}
                                                            </Text>
                                                        )}
                                                        <OptionsTable<
                                                            SellerFinanceBatchDetail['payouts'][number]
                                                        >
                                                            columns={payoutDetailColumns}
                                                            data={batchDetail.payouts}
                                                            emptyMessage="Нет строк выплат в пачке"
                                                        />
                                                    </Stack>
                                                ) : (
                                                    <Text size="sm" c="dimmed">
                                                        Не удалось загрузить детали
                                                    </Text>
                                                )}
                                            </div>
                                        )}
                                    />
                                    </div>
                                </section>
                            </Tabs.Panel>
                        </Tabs>
                    </div>
                </div>
            </div>

            <Modal
                opened={clearModalOpen}
                onClose={() => setClearModalOpen(false)}
                title="Удалить все выплаты?"
                centered
            >
                <Text size="sm" mb="md">
                    Будут удалены все записи журналов выплат и все пачки. Это действие для локального теста и
                    повторного запуска мока за ту же неделю.
                </Text>
                <Group justify="flex-end">
                    <Button variant="default" onClick={() => setClearModalOpen(false)}>
                        Отмена
                    </Button>
                    <Button
                        color="red"
                        loading={clearAllPayouts.isPending}
                        onClick={() => clearAllPayouts.mutate()}
                    >
                        Удалить всё
                    </Button>
                </Group>
            </Modal>
        </div>
    );
}
